import { Component, OnInit, TemplateRef } from '@angular/core';
import { ActivatedRoute } from '@angular/router';
import { NgbOffcanvas } from '@ng-bootstrap/ng-bootstrap';
import { UserService } from 'src/app/services/user/user.service';
import * as alertifyjs from 'alertifyjs';
import { MerchantService } from 'src/app/services/merchant/merchant.service';

interface Quote {
  _id?: string;
  code: string;
  order?: number;
  total_installments?: number; // hoy el API no lo envía; si llega, se muestra «Cuota N de M»
  scheduledPaymentDate: Date;
  shop: {
    short_name: string;
  };
  invoice: {
    code: string;
    identifier: string;
  };
  pendigAmount: number;
  amount_fee?: number;
  expired: boolean;
  isChecked: boolean;

  // Campos opcionales de simulación de pago por conciliar (vista informativa).
  // Si no llegan, la vista renderiza igual que siempre.
  sim_original?: number;
  sim_applied?: number;
  sim_remaining?: number;
  sim_completed?: boolean;
}

interface PendingQuotesResponse {
  res: {
    installments: Quote[];
    payment_margin: number;
    penalty_fees: number;
    reactivation_fees: number;
    total_fees: number;
    amount_fees: number;
    in_domiciliation_process: boolean;
  };
}

@Component({
  selector: 'app-client-installments-view',
  templateUrl: './client-installments-view.component.html',
  styleUrls: ['./client-installments-view.component.css']
})
export class ClientInstallmentsViewComponent implements OnInit {
  
  // Variables principales
  personCode: string = '';
  quoteList: Quote[] = [];
  totalAmount: number = 0;
  totalSelected: number = 0;
  totalFee: number = 0;
  totalPenaltyFee: number = 0;
  totalReactivationFee: number = 0;
  totalWithoutReactivation: number = 0; // Total sin fee de reactivación
  totalWithReactivation: number = 0; // Total con fee de reactivación
  // Totales después de aplicar la cobertura del pago por conciliar
  // (solo para penalidad + cuotas seleccionadas + C004).
  totalWithoutReactivationAfterConciliar: number = 0;
  totalWithReactivationAfterConciliar: number = 0;
  totalCoveredWithoutReact: number = 0;
  totalCoveredWithReact: number = 0;
  isLoading: boolean = true;
  paymentMargin: number = 0;
  inDomiciliationProcess: boolean = false;
  
  // Estados
  hasError: boolean = false;
  errorMessage: string = '';
  
  // Variables para tasa de cambio
  exchangeRate: number = 0;
  loadingRate: boolean = false;

  // Simulación de pago por conciliar (Vista informativa; opcional).
  // Si la 2da llamada falla, queda en defaults y la vista no muestra nada extra.
  conciliarTotal: number = 0;
  conciliarC001Sim: any[] = [];
  conciliarC004Sim: any[] = [];
  // Lista detallada de pagos por conciliar (cada Payment doc del cliente).
  pendingPayments: any[] = [];
  // Agregados de cobertura por bucket — para mostrar en banner y en Resumen de Pago.
  sumAppliedC001: number = 0;
  sumAppliedInstallments: number = 0;
  sumAppliedC004: number = 0;
  sumLeftover: number = 0;
  countInstallmentsCovered: number = 0;

  // Params para el offcanvas de detalle de pago (mismo patrón que contact-financial-payments)
  params_payment: any = {};

  constructor(
    private activatedRoute: ActivatedRoute,
    private _userService: UserService,
    private offcanvasService: NgbOffcanvas,
    private _merchantSrv: MerchantService
  ) {}

  // ══ COMERCIO DE CADA CUOTA (multicomercio) ══════════════════════════════
  // Solo informativo: el cliente es de CrediTotal y acá se ven TODAS sus
  // cuotas. La cuota hereda el comercio de su crédito; sin marca es MundoTotal.
  private nombresComercio = new Map<string, string>();

  private cargarNombresComercio = async (): Promise<void> => {
    try {
      const rep: any = await this._merchantSrv.getMerchants({});
      (rep?.merchants || rep || []).forEach((m: any) => {
        if (m?.code) this.nombresComercio.set(String(m.code).toUpperCase(), m.name || m.code);
      });
    } catch { /* sin nombres: se muestra el código */ }
  };

  comercioDeCuota(quote: any): string {
    const code = String(quote?.merchant_code || quote?.invoice?.merchant_code || 'MT').toUpperCase();
    return this.nombresComercio.get(code) || (code === 'MT' ? 'MundoTotal' : code);
  }

  /**
   * Abre el offcanvas inferior con el detalle de un pago.
   * Reusa el mismo patrón que contact-financial-payments y person-fee-payments:
   * el template `paymentcontainer` incluye <app-person-payments>.
   */
  openPaymentDetail = (content: TemplateRef<any>, paymentCode: string) => {
    try {
      this.params_payment = {
        person_code: this.personCode,
        code_payment: paymentCode
      };
      this.offcanvasService.open(content, {
        position: 'bottom',
        keyboard: false
      });
    } catch (error) {
      console.error('openPaymentDetail error:', error);
    }
  };

  paymentDetailResponse = (_event: any) => {
    // Callback del componente person-payments (no requiere acción aquí).
  };

  ngOnInit(): void {
    this.cargarNombresComercio();
    this.activatedRoute.params.subscribe(params => {
      this.personCode = params['code'];
      if (this.personCode) {
        this.loadPendingQuotes();
        this.loadExchangeRate();
      } else {
        this.hasError = true;
        this.errorMessage = 'Código de persona no válido';
        this.isLoading = false;
      }
    });
  }

  /**
   * Carga las cuotas pendientes del cliente desde el API
   */
  private async loadPendingQuotes(): Promise<void> {
    try {
      this.isLoading = true;
      this.hasError = false;

      console.log('🔍 Calling API with personCode:', this.personCode);
      console.log('🔍 Token from localStorage:', localStorage.getItem('x-token'));

      // 2 llamadas en paralelo: la existente (intacta) + la nueva simulación.
      // La simulación es opcional — si falla, devuelve shape vacío y la vista
      // se renderiza igual que siempre.
      const [response, simulation] = await Promise.all([
        this.getClientPendingQuotes(this.personCode),
        this._userService.getConciliarSimulation(this.personCode).catch(() => null)
      ]);

      console.log('✅ API Response:', response);
      console.log('🧮 Simulation Response:', simulation);

      if (response && response.res) {
        this.processQuotesData(response.res);
        // Anexa simulación si llegó válida (defensive — no rompe si está vacía).
        if (simulation && typeof simulation === 'object') {
          this.mergeSimulationData(simulation);
        }
      } else {
        throw new Error('No se pudieron cargar las cuotas pendientes');
      }

    } catch (error: any) {
      console.error('❌ Error loading pending quotes:', error);
      this.hasError = true;
      this.errorMessage = error.message || 'Error al cargar las cuotas pendientes';
      alertifyjs.error('Error al cargar las cuotas pendientes');
    } finally {
      this.isLoading = false;
    }
  }

  /**
   * Anexa la simulación de pago por conciliar a las cuotas ya cargadas.
   * Solo agrega campos opcionales sim_* — no muta ni reemplaza nada del payload original.
   * Si el merge no encuentra match por _id, simplemente no anexa (sin error).
   */
  /**
   * Calcula la distribución del pago por conciliar usando los valores que YA están
   * visibles en la UI (totalPenaltyFee, quoteList, totalReactivationFee).
   * Esto evita cualquier riesgo de inconsistencia con lo que ve el ejecutivo.
   *
   * Orden de prelación (espejo de reconciledPaymentAndInvoceService):
   *   1) Penalidad de incumplimiento (C001)
   *   2) Cuotas, en el orden en que vienen (ya ordenadas por fecha por el backend)
   *   3) Penalidad de reactivación (C004)
   *   4) Sobrante → saldo a favor
   *
   * Del endpoint nuevo solo tomamos el monto total (payment_conciliar_total).
   */
  private mergeSimulationData(sim: any): void {
    this.conciliarTotal = Number(sim?.payment_conciliar_total || 0);
    this.pendingPayments = Array.isArray(sim?.pending_payments) ? sim.pending_payments : [];
    const r2 = (x: number) => Math.round((Number(x) || 0) * 100) / 100;

    // === Restaurar dueBalance crudo en las cuotas existentes ===
    // El endpoint principal (lateUpcomingInstPayments) le aplica su propio
    // `descontando` loop al pendigAmount (líneas 18113-18130 de invoice_credit.js)
    // y devuelve la cuota con el valor ya reducido por el pago por conciliar.
    // Si después nuestra simulación distribuye conciliar OTRA VEZ sobre ese
    // valor pre-descontado, contamos el pago por conciliar dos veces.
    // Por eso para cada cuota que también esté en la simulación, restituimos
    // el `dueBalance` real del installment y dejamos que nuestra distribución
    // sea la única fuente de verdad para "qué cubre el pago por conciliar".
    const simInstallments: any[] = Array.isArray(sim?.installments) ? sim.installments : [];
    const simById = new Map<string, any>();
    simInstallments.forEach((s: any) => {
      if (s && s._id) simById.set(String(s._id), s);
    });
    this.quoteList.forEach((q: any) => {
      const s = simById.get(String(q._id || ''));
      if (s && typeof s.dueBalance === 'number') {
        q.pendigAmount = Number(s.dueBalance) || 0;
      }
    });

    // === Recuperar cuotas dropeadas por el endpoint principal ===
    // El endpoint actual elimina del response las cuotas que el pago por conciliar
    // cubriría completamente (líneas 18119-18130 de invoice_credit.js). Las
    // recuperamos desde la simulación para que el ejecutivo SIGA viéndolas
    // marcadas como "Quedaría cubierta completamente".
    if (simInstallments.length > 0) {
      const existingIds = new Set(this.quoteList.map((q: any) => String(q._id || '')));
      const today = new Date();
      today.setHours(0, 0, 0, 0);

      simInstallments.forEach((s: any) => {
        const sId = String(s._id || '');
        if (sId && !existingIds.has(sId)) {
          // Cuota que el endpoint principal dropeó — la agregamos al listado
          // usando los datos del simulador (que incluye shop e invoice).
          const sched = s.scheduledPaymentDate ? new Date(s.scheduledPaymentDate) : null;
          const expired = sched ? sched.getTime() < today.getTime() : false;
          this.quoteList.push({
            _id: sId,
            code: s.code || '',
            scheduledPaymentDate: sched as any,
            shop: { short_name: s.shop_short_name || '' },
            invoice: { code: s.invoice_code || '', identifier: s.invoice_identifier || '' },
            pendigAmount: Number(s.dueBalance) || 0,
            expired,
            isChecked: false
          } as Quote);
        }
      });

      // Re-ordenar por fecha asc para mantener el orden visual coherente.
      this.quoteList.sort((a: any, b: any) => {
        const da = a.scheduledPaymentDate ? new Date(a.scheduledPaymentDate).getTime() : 0;
        const db = b.scheduledPaymentDate ? new Date(b.scheduledPaymentDate).getTime() : 0;
        return da - db;
      });

      // Re-inicializar selección: tras recuperar cuotas dropeadas y re-ordenar,
      // la cuota que estaba `isChecked` en el índice 0 puede haber cambiado de
      // posición. Volvemos a marcar el índice 0 visible para que "Monto
      // seleccionado" coincida con lo que el usuario ve marcado.
      this.initializeQuotesState();
    }

    // Reset (importante por si el método se llama después de cambios)
    this.sumAppliedC001 = 0;
    this.sumAppliedInstallments = 0;
    this.sumAppliedC004 = 0;
    this.sumLeftover = 0;
    this.countInstallmentsCovered = 0;
    this.quoteList.forEach((q: Quote) => {
      q.sim_applied = 0;
      q.sim_remaining = q.pendigAmount || 0;
      q.sim_completed = false;
      q.sim_original = q.pendigAmount || 0;
    });

    if (this.conciliarTotal <= 0) return;

    let remaining = this.conciliarTotal;

    // 1) C001 (penalidad de incumplimiento) como bucket
    this.sumAppliedC001 = r2(Math.min(remaining, Number(this.totalPenaltyFee) || 0));
    remaining = r2(Math.max(0, remaining - this.sumAppliedC001));

    // 2) Cuotas en orden
    if (remaining > 0) {
      this.quoteList.forEach((q: Quote) => {
        if (remaining <= 0) return;
        const due = Number(q.pendigAmount) || 0;
        const applied = r2(Math.min(remaining, due));
        if (applied > 0) {
          q.sim_original = due;
          q.sim_applied = applied;
          q.sim_remaining = r2(due - applied);
          q.sim_completed = applied >= due;
          this.sumAppliedInstallments = r2(this.sumAppliedInstallments + applied);
          this.countInstallmentsCovered++;
          remaining = r2(Math.max(0, remaining - applied));
        }
      });
    }

    // 3) C004 (penalidad de reactivación)
    if (remaining > 0) {
      this.sumAppliedC004 = r2(Math.min(remaining, Number(this.totalReactivationFee) || 0));
      remaining = r2(Math.max(0, remaining - this.sumAppliedC004));
    }

    // 4) Sobrante
    this.sumLeftover = r2(remaining);

    // Re-computar totales para reflejar la cobertura del pago por conciliar.
    this.calculateTotalAmount();
  }

  /**
   * Procesa los datos de cuotas recibidos del API
   */
  private processQuotesData(data: any): void {
    this.inDomiciliationProcess = data.in_domiciliation_process || false;
    this.paymentMargin = data.payment_margin || 0;
    this.totalPenaltyFee = data.penalty_fees || 0;
    this.totalReactivationFee = data.reactivation_fees || 0;
    this.totalFee = data.total_fees || data.amount_fees || 0;
    this.quoteList = data.installments || [];
    
    // Inicializar el estado de las cuotas
    this.initializeQuotesState();
    this.calculateTotalAmount();
  }

  /**
   * Inicializa el estado de selección de las cuotas
   * Marca la primera cuota como seleccionada por defecto
   */
  private initializeQuotesState(): void {
    this.quoteList.forEach((quote, index) => {
      quote.isChecked = index === 0; // Solo la primera cuota seleccionada
    });
  }

  /**
   * Maneja el cambio de estado de los checkboxes
   * Implementa la lógica de selección secuencial
   */
  onCheckboxChange(selectedQuote: Quote, selectedIndex: number): void {
    // Toggle del checkbox seleccionado
    selectedQuote.isChecked = !selectedQuote.isChecked;
    
    // Si se deselecciona una cuota, deseleccionar todas las posteriores
    if (!selectedQuote.isChecked) {
      this.quoteList
        .slice(selectedIndex + 1)
        .forEach(quote => quote.isChecked = false);
    }
    
    this.calculateTotalAmount();
  }

  /**
   * Calcula el monto total a pagar
   * Suma las cuotas seleccionadas más los fees
   */
  private calculateTotalAmount(): void {
    this.totalSelected = 0;

    this.quoteList.forEach(quote => {
      if (quote.isChecked) {
        this.totalSelected += quote.pendigAmount;
      }
    });

    // Total sin fee de reactivación (cuotas + penalidad)
    this.totalWithoutReactivation = this.totalSelected + this.totalPenaltyFee;

    // Total con fee de reactivación (cuotas + penalidad + reactivación)
    this.totalWithReactivation = this.totalSelected + this.totalPenaltyFee + this.totalReactivationFee;

    // Total general (mantener compatibilidad)
    this.totalAmount = this.totalWithReactivation;

    // === Totales después de aplicar el pago por conciliar ===
    // Suma cubierta = lo aplicado a la penalidad + lo aplicado a las cuotas
    // SELECCIONADAS + lo aplicado a C004. Si no hay pago por conciliar,
    // todo queda en 0 y los totales "afterConciliar" igualan los originales.
    const r2 = (x: number) => Math.round((Number(x) || 0) * 100) / 100;

    let coveredOnSelectedCuotas = 0;
    this.quoteList.forEach(quote => {
      if (quote.isChecked) {
        coveredOnSelectedCuotas += Number(quote.sim_applied) || 0;
      }
    });
    coveredOnSelectedCuotas = r2(coveredOnSelectedCuotas);

    this.totalCoveredWithoutReact = r2(this.sumAppliedC001 + coveredOnSelectedCuotas);
    this.totalCoveredWithReact = r2(this.sumAppliedC001 + coveredOnSelectedCuotas + this.sumAppliedC004);

    this.totalWithoutReactivationAfterConciliar = r2(Math.max(0, this.totalWithoutReactivation - this.totalCoveredWithoutReact));
    this.totalWithReactivationAfterConciliar = r2(Math.max(0, this.totalWithReactivation - this.totalCoveredWithReact));
  }

  /**
   * Verifica si se pueden seleccionar las cuotas anteriores
   * Para mantener el orden secuencial
   */
  canSelectQuote(currentIndex: number): boolean {
    // La primera cuota siempre se puede seleccionar
    if (currentIndex === 0) return true;
    
    // Para las demás, verificar que todas las anteriores estén seleccionadas
    for (let i = 0; i < currentIndex; i++) {
      if (!this.quoteList[i].isChecked) {
        return false;
      }
    }
    return true;
  }

  /**
   * Selecciona o deselecciona todas las cuotas
   */
  selectAllQuotes(): void {
    const allSelected = this.quoteList.every(quote => quote.isChecked);
    
    if (allSelected) {
      // Si todas están seleccionadas, deseleccionar todas
      this.quoteList.forEach(quote => {
        quote.isChecked = false;
      });
    } else {
      // Si no todas están seleccionadas, seleccionar todas
      this.quoteList.forEach(quote => {
        quote.isChecked = true;
      });
    }
    
    this.calculateTotalAmount();
  }

  /**
   * Determina el color del badge según el estado de la cuota
   */
  getQuoteStatusColor(quote: Quote): string {
    if (quote.expired) {
      return 'bg-red-100 text-red-800';
    }
    return 'bg-green-100 text-green-800';
  }

  /**
   * Determina el texto del badge según el estado de la cuota
   */
  getQuoteStatusText(quote: Quote): string {
    if (quote.expired) {
      return 'Vencida';
    }
    return 'Al día';
  }

  /**
   * Carga la tasa de cambio actual
   */
  private async loadExchangeRate(): Promise<void> {
    try {
      this.loadingRate = true;
      console.log('💱 Loading exchange rate...');
      
      // Usar el mismo formato que en el componente post
      const data = { subscriptor: 1 };
      
      const response = await this._userService.getRate(data);
      console.log('💱 Raw response:', response);
      
      if (response && response.res) {
        // El rate viene en response.res, no directamente en response
        const rate = response.res;
        if (rate && rate.amount) {
          this.exchangeRate = parseFloat(rate.amount);
          console.log('💱 Exchange rate loaded successfully:', this.exchangeRate);
        } else {
          console.warn('⚠️ No amount found in rate object:', rate);
          this.exchangeRate = 1;
        }
      } else {
        console.warn('⚠️ No rate data found in response:', response);
        this.exchangeRate = 1;
      }
      
    } catch (error: any) {
      console.error('❌ Error loading exchange rate:', error);
      this.exchangeRate = 1; // Valor por defecto en caso de error
    } finally {
      this.loadingRate = false;
    }
  }

  /**
   * Convierte USD a Bolívares
   */
  convertToBolivars(usdAmount: number): number {
    return usdAmount * this.exchangeRate;
  }

  /**
   * Formatea el monto en bolívares
   */
  formatBolivars(amount: number): string {
    return this.convertToBolivars(amount).toLocaleString('es-VE', {
      minimumFractionDigits: 2,
      maximumFractionDigits: 2
    });
  }

  /**
   * Servicio para obtener cuotas pendientes del cliente
   * Usa el método del UserService existente
   */
  private async getClientPendingQuotes(personCode: string): Promise<any> {
    try {
      return await this._userService.getClientPendingQuotes(personCode);
    } catch (error) {
      console.error('Error fetching pending quotes:', error);
      throw error;
    }
  }

  /**
   * Calcula los días restantes para el pago
   */
  getRemainingDays(scheduledDate: Date): number {
    const today = new Date();
    const paymentDate = new Date(scheduledDate);
    const diffTime = paymentDate.getTime() - today.getTime();
    const diffDays = Math.ceil(diffTime / (1000 * 60 * 60 * 24));
    return diffDays;
  }

  /**
   * Formatea el texto de días restantes
   */
  getRemainingDaysText(scheduledDate: Date): string {
    const days = this.getRemainingDays(scheduledDate);
    
    if (days < 0) {
      return `${Math.abs(days)} día(s) vencida`;
    } else if (days === 0) {
      return 'Vence hoy';
    } else if (days === 1) {
      return 'Vence mañana';
    } else {
      return `${days} días restantes`;
    }
  }

  /**
   * Reintenta cargar las cuotas en caso de error
   */
  retryLoad(): void {
    this.loadPendingQuotes();
  }

  /**
   * Copia el código de factura al portapapeles
   */
  async copyInvoiceCode(invoiceCode: string): Promise<void> {
    try {
      await navigator.clipboard.writeText(invoiceCode);
      alertifyjs.success(`Código de crédito copiado: ${invoiceCode}`);
    } catch (error) {
      console.error('Error al copiar código de crédito:', error);
      // Fallback para navegadores que no soportan navigator.clipboard
      this.fallbackCopyToClipboard(invoiceCode);
    }
  }

  /**
   * Método fallback para copiar al portapapeles
   */
  private fallbackCopyToClipboard(text: string): void {
    try {
      const textArea = document.createElement('textarea');
      textArea.value = text;
      document.body.appendChild(textArea);
      textArea.select();
      document.execCommand('copy');
      document.body.removeChild(textArea);
      alertifyjs.success(`Código de crédito copiado: ${text}`);
    } catch (error) {
      console.error('Error en fallback de copiado:', error);
      alertifyjs.error('No se pudo copiar el código');
    }
  }
} 